import React, {forwardRef, useEffect, useMemo, useRef} from "react";
import type {ForwardedRef, Ref, ChangeEvent, ClipboardEvent, FormEvent, ReactNode, RefObject, DetailedHTMLProps, FormHTMLAttributes, KeyboardEvent} from "react";

import {GFormContextProvider, useFormStore} from "./form-context";
import {_merge, _hasSubmitter, _mergeRefs, _buildFormState} from "./helpers";
import {useFormSelector} from "./hooks";
import type {GFormState} from "./state";
import type {GChangeEvent, IForm, PartialForm} from "./form";
import type {GInputState} from "./fields";
import type {GValidators} from "./validations";

type FormRendererProps<T> = GFormProps<T> & { formRef: RefObject<HTMLFormElement|null> };

if (__DEV__) {
    var _warnedOptimizedDefault = false, warnedNoSubmitter = false;
}

const FormRenderer = forwardRef<HTMLFormElement, FormRendererProps<any>>(
    <T, >({
        stateRef,
        onSubmit,
        onReset,
        onChange,
        onPaste,
        onKeyDown,
        onKeyUp,
        children,
        onInit,
        formRef,
        ...rest
    }: FormRendererProps<T>, ref: ForwardedRef<HTMLFormElement>) => {
        const {handlers, getState, optimized} = useFormStore();
        const fields = useFormSelector(state => state.fields) as IForm<T>;

        const formComponent = useMemo(() => {
            const state = _buildFormState(fields, formRef.current!, handlers);
            const formChildren = typeof children === 'function' ? children(state) : children;

            const _onSubmit = (e: FormEvent<HTMLFormElement>) => {
                if (!handlers._validateForm()) {
                    e.preventDefault();
                    return;
                }
                const state = _buildFormState(getState<T>().fields, formRef.current!, handlers);
                if (onSubmit) {
                    onSubmit(state, e);
                }
            };

            const _onReset = (e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                handlers._resetForm();
                if (onReset) onReset(state, e);
            };

            let _onPaste, _onChange, _onKeyDown, _onKeyUp;

            if (onPaste) {
                _onPaste = (e: ClipboardEvent<HTMLFormElement>) => onPaste(state, e);
            }

            if (onKeyDown) {
                _onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => onKeyDown(state, e);
            }

            if (onKeyUp) {
                _onKeyUp = (e: KeyboardEvent<HTMLFormElement>) => onKeyUp(state, e);
            }

            if (stateRef) stateRef.current = state;

            if (optimized) {
                if (onChange) {
                    _onChange = (e: GChangeEvent<HTMLFormElement>, unknown?: { value: unknown } | string | number) => {
                        handlers._updateInputHandler(state[e.target.name as keyof T], e, unknown);
                        onChange(state, e);
                    };
                } else {
                    _onChange = (e: GChangeEvent<HTMLFormElement>, unknown?: { value: unknown } | string | number) => {
                        handlers._updateInputHandler(state[e.target.name as keyof T], e, unknown);
                    };
                }
                return (
                    <form {...rest}
                        ref={_mergeRefs(ref, formRef)}
                        onPaste={_onPaste}
                        onKeyDown={_onKeyDown}
                        onKeyUp={_onKeyUp}
                        onBlur={(e: GChangeEvent<HTMLFormElement>) => handlers._blurHandler(state[e.target.name as keyof T], e)}
                        onInvalid={(e: ChangeEvent<HTMLFormElement>) => {
                            e.preventDefault(); // hide default browser validation tooltip
                            handlers._viHandler(state[e.target.name as keyof T], e);
                        }}
                        onChange={_onChange}
                        onSubmit={_onSubmit}
                        onReset={_onReset}>
                        {formChildren}
                    </form>
                );
            }

            if (onChange) {
                _onChange = (e: GChangeEvent<HTMLFormElement>) => onChange(state, e);
            }

            return (
                <form {...rest}
                    ref={_mergeRefs(ref, formRef)}
                    onSubmit={_onSubmit}
                    onReset={_onReset}
                    onChange={_onChange}
                    onPaste={_onPaste}
                    onKeyDown={_onKeyDown}
                    onKeyUp={_onKeyUp}>
                    {formChildren}
                </form>
            );
        }, [children, fields]);

        useEffect(() => {
            const initialStateFields = getState<T>().fields;
            const state = _buildFormState<T>(initialStateFields, formRef.current!, handlers);

            if (__DEV__ && !warnedNoSubmitter && !_hasSubmitter(formRef.current)) {
                warnedNoSubmitter = true;
                console.warn(`DEV ONLY - [No Submit Button] - you have created a form without a button type=submit, this will prevent the onSubmit event from being fired.\nif you have a button with onClick event that handle the submission of the form then ignore this warning\nbut don't forget to manually invoke the checkValidity() function to check if the form is valid before perfoming any action, for example:\nif (formState.checkValidity()) { \n\t//do somthing\n}\n`);
            }

            if (__DEV__ && !optimized && !_warnedOptimizedDefault) {
                _warnedOptimizedDefault = true;
                console.warn(`DEV ONLY - [Optimized Default Changing] - this <GForm> is not using 'optimized'. In a future release forms will be optimized (change/blur/invalid delegated to the <form>) by default.\nTo adopt the new behavior now and silence this notice, pass 'optimized' to <GForm>:\n<GForm optimized ...>\n`);
            }

            if (onInit) {
                const changes = onInit(state);
                if (changes) {
                    const _handler = (_c: void | PartialForm<T>) => {
                        handlers._dispatchChanges({
                            fields: _merge<IForm<T> & {
                                [key: string]: GInputState;
                            }>({}, state, _c)
                        });
                        if (_c) handlers._seedInitial(Object.keys(_c));
                    };
                    if (changes instanceof Promise) {
                        changes.then(_handler);
                    } else _handler(changes);
                }
            }
        }, []);

        return formComponent;
    }
) as <T>(props: FormRendererProps<T> & { ref?: Ref<HTMLFormElement> }) => React.ReactElement | null;

export type GFormProps<T> =
    Omit<DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>, 'onSubmit' | 'onReset' | 'onPaste' | 'onChange' | 'onKeyUp' | 'onKeyDown' | 'children'>
    & {
    children?: ReactNode | ReactNode[] | ((state: GFormState<T>) => ReactNode | ReactNode[]);
    /** @param stateRef - pass a ref which will points to the current state of the form (optional). */
    stateRef?: RefObject<GFormState<T> | undefined>;
    /** @param onSubmit - a handler for the form submission (optional). */
    onSubmit?: (state: GFormState<T>, e: FormEvent<HTMLFormElement>) => void;
    /** @param onReset - runs after a native form reset restores every field to its initial value (optional). */
    onReset?: (state: GFormState<T>, e: FormEvent<HTMLFormElement>) => void;
    /** @param onChange - register onChange handler (optional). */
    onChange?: (state: GFormState<T>, e: FormEvent<HTMLFormElement>) => void;
    /** @param onPaste - register onPaste handler (optional). */
    onPaste?: (state: GFormState<T>, e: ClipboardEvent<HTMLFormElement>) => void;
    /** @param onKeyUp - register onKeyUp handler (optional). */
    onKeyUp?: (state: GFormState<T>, e: KeyboardEvent<HTMLFormElement>) => void;
    /** @param onKeyDown - register onKeyDown handler (optional). */
    onKeyDown?: (state: GFormState<T>, e: KeyboardEvent<HTMLFormElement>) => void;
    /** @param validators - an object for handling validations (optional). */
    validators?: GValidators<T>;
    /** @param onInit - execute a handler once the form has initialized (optional). */
    onInit?: (state: GFormState<T>) => void | PartialForm<T> | Promise<void | PartialForm<T>>;
    /** @param optimized - enable optimization by registering the required handlers on the form itself.
     * @see {@link https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Building_blocks/Events#event_delegation|EventDelegation}
     * @optional
     */
    optimized?: boolean;
};

/**
 * build dynamic forms with validations.
 * @link Docs - https://gform-react.vercel.app
 * @link Npm - https://www.npmjs.com/package/gform-react
 */
export const GForm = forwardRef<HTMLFormElement, GFormProps<any>>(
    <T, >({children, validators, optimized, ...props}: GFormProps<T>, ref: ForwardedRef<HTMLFormElement>) => {
        const initialState = useMemo(() => ({ fields: {} }), []);
        const formRef = useRef<HTMLFormElement>(null);

        return (
            <GFormContextProvider initialState={initialState} formRef={formRef} validators={validators} optimized={optimized}>
                <FormRenderer ref={ref} formRef={formRef} {...props}>
                    {children}
                </FormRenderer>
            </GFormContextProvider>
        );
    }
) as <T>(props: GFormProps<T> & { ref?: React.Ref<HTMLFormElement> }) => React.ReactElement | null;